import { useEffect } from 'react';
import { ServerEvent } from '@anon-chat/shared';
import { renewSession } from './api';
import { connectSocket, disconnectSocket } from './socket';
import { haptic } from './telegram';
import { useChatStore } from '@/store/chat.store';

/** С чего начинается пауза перед повторным подключением. */
const RECOVERY_BASE_DELAY_MS = 500;
/** Дальше какой паузы не растёт: человек не должен ждать дольше полуминуты. */
const RECOVERY_MAX_DELAY_MS = 30_000;

/**
 * Единственное место, где сокет-события превращаются в изменения состояния.
 * Компоненты подписываются на store, а не на сокет: экран не зависит от
 * транспорта и легко тестируется.
 *
 * Типы обработчиков выводятся из карты `ServerToClientEvents` в shared —
 * несовпадение с контрактом сервера станет ошибкой компиляции.
 */
export function useSocketEvents(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const socket = connectSocket();
    const store = useChatStore.getState;
    store().onConnectionChange(socket.connected);

    /*
      Восстановление после окончательного отказа: обновляем пару токенов и
      подключаемся заново. Задержка растёт вдвое с каждой попыткой — если
      причина не в токене, а в недоступности сервера, попытки не должны
      превращаться в собственную нагрузку на него.
    */
    let recoveryTimer: number | null = null;
    let recoveryAttempt = 0;

    const scheduleRecovery = (): void => {
      if (recoveryTimer !== null) {
        return;
      }

      recoveryAttempt += 1;
      const delay = Math.min(
        RECOVERY_MAX_DELAY_MS,
        RECOVERY_BASE_DELAY_MS * 2 ** (recoveryAttempt - 1),
      );

      recoveryTimer = window.setTimeout(() => {
        recoveryTimer = null;
        void renewSession().then(() => {
          if (!socket.connected) {
            socket.connect();
          }
        });
      }, delay);
    };

    socket.on(ServerEvent.MATCH_FOUND, (payload) => {
      haptic.notify('success');
      store().onMatchFound(payload.chat);
    });

    socket.on(ServerEvent.QUEUE_POSITION, (payload) => {
      store().onQueuePosition(payload);
    });

    socket.on(ServerEvent.QUEUE_LEFT, () => {
      store().onQueueLeft();
    });

    socket.on(ServerEvent.MESSAGE, (message) => {
      haptic.impact('light');
      store().onIncomingMessage(message);
    });

    socket.on(ServerEvent.MEDIA, (message) => {
      store().onIncomingMessage(message);
    });

    socket.on(ServerEvent.TYPING, (payload) => {
      store().onPartnerTyping(payload.isTyping);
    });

    socket.on(ServerEvent.READ, (payload) => {
      store().onReadReceipt(payload.messageId);
    });

    socket.on(ServerEvent.PARTNER_STATUS, (payload) => {
      store().onPartnerStatus(payload.chatId, payload.isOnline, payload.authAway);
    });

    socket.on(ServerEvent.CHAT_CLOSED, (payload) => {
      haptic.impact('medium');
      store().onChatClosed(payload.reason, payload.requeued);
    });

    // Приходит только тому, кто остался: диалог закрыт не по его воле.
    // Без этого чат просто исчезал с экрана без объяснения.
    socket.on(ServerEvent.USER_LEFT, () => {
      store().onPartnerLeft();
    });

    socket.on(ServerEvent.FRIEND_REQUESTED, (payload) => {
      haptic.notify('success');
      store().onFriendRequested(payload);
    });

    socket.on(ServerEvent.FRIEND_ACCEPTED, (payload) => {
      haptic.notify('success');
      store().onFriendAccepted(payload);
    });

    socket.on(ServerEvent.FRIEND_DECLINED, (payload) => {
      store().onFriendDeclined(payload.chatId);
    });

    socket.on(ServerEvent.FRIEND_ALREADY, (payload) => {
      store().onFriendAlready(payload.chatId);
    });

    socket.on(ServerEvent.FRIEND_REMOVED, (payload) => {
      store().onFriendRemoved(payload.chatId);
    });

    socket.on(ServerEvent.ERROR, (payload) => {
      store().setError(payload.code);
    });

    /*
      Подключение не состоялось. Молчать нельзя: без объяснения пользователь
      смотрит на неподвижный экран и не понимает, чего ждёт. Код приходит от
      сервера в `data`; если до сервера не дошло вовсе, причина — недоступность,
      а не отказ в доступе.

      Отказ на рукопожатии socket.io считает окончательным и сам больше не
      подключается (`socket.active === false`). Для протухшего access-токена это
      означало мёртвый сокет при живом HTTP: страница открывается, запросы
      проходят — их слой обновляет пару сам, — а поиск не начинается и сообщения
      не уходят, пока человек не перезагрузит страницу. Случалось это чаще всего
      при первом открытии после перерыва: в хранилище лежит токен, который успел
      истечь.
    */
    socket.on('connect_error', (error: Error & { data?: { code?: string } }) => {
      store().setError(error.data?.code ?? 'SERVICE_UNAVAILABLE');
      if (!socket.active) {
        scheduleRecovery();
      }
    });

    /*
      Первое подключение состояние уже восстановило (см. App), а вот каждое
      следующее означает, что связи какое-то время не было. За это время диалог
      могли закрыть, и событие до нас не дошло — состояние берём у сервера.
    */
    let connectedBefore = socket.connected;
    socket.on('connect', () => {
      store().setError(null);
      store().onConnectionChange(true);
      recoveryAttempt = 0;

      if (connectedBefore) {
        void store().syncWithServer();
      }
      connectedBefore = true;
    });

    // Обрыв собственного соединения виден пользователю: иначе отправленные
    // сообщения молча краснеют, и непонятно, дело в собеседнике или в связи.
    socket.on('disconnect', () => {
      store().onConnectionChange(false);
    });

    /*
      Возвращение из фона. Свёрнутое приложение и заблокированный телефон
      усыпляют вкладку: сокет рвётся молча, а иногда остаётся «живым» лишь на
      бумаге. Поэтому при возвращении соединение поднимается принудительно, а
      состояние сверяется с сервером — иначе человек видит диалог, которого
      уже нет.
    */
    const resync = () => {
      if (!socket.connected) {
        socket.connect();
      }
      void store().syncWithServer();
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        resync();
      }
    };

    document.addEventListener('visibilitychange', onVisible);

    /*
      Возвращение сети. Ждать, пока сокет сам заметит обрыв, нельзя: он делает
      это по таймауту пинга, а это до сорока пяти секунд молчания в диалоге,
      которого, возможно, уже нет. Браузер сообщает о восстановлении связи
      сразу, и этого достаточно, чтобы сверить состояние по HTTP.
    */
    window.addEventListener('online', resync);

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', resync);
      if (recoveryTimer !== null) {
        window.clearTimeout(recoveryTimer);
      }
      socket.removeAllListeners();
      disconnectSocket();
    };
  }, [enabled]);
}
